import { createSignal, For, JSXElement, Show } from 'solid-js';
import { Button } from '~/components/ui/Button';
import Modal from '~/components/ui/Modal';
import { ModalSizes, ModalTypes } from '~/components/ui/Modal/Modal.tsx';
import { useAppData } from '~/components/AppData';
import FSAOnboardingLogo from '~/assets/fsa-onboarding-logo.svg';
import './fsaOnboardingModal.module.scss';
import { Localized } from '~/i18n';
import { ArrowUpRightIcon, CircleCheckIcon } from 'lucide-solid';
import Link from '~/components/Link';
import { externalLinks } from '~/consts.ts';
import {
  EnvironmentTypes,
  getEnvActiveSsoConnectionListQueryKey,
  retryAuthorizedRequests,
  searchConnections,
  SearchConnectionsResponse
} from '~/api';
import {
  enableFSAWithSuccessToast,
  FSA_FEATURES_TRANSLATION_KEYS,
  getTranslationKey,
  logAndToastFSAError
} from '~/pages/FSAOnboarding/utils.tsx';
import { useIntl } from '@cookbook/solid-intl';
import { useFSAModalContext } from '~/pages/FSAOnboarding/FSAModalContext.tsx';
import { useToast } from '~/components/ui/Toast';
import { createQuery } from '@tanstack/solid-query';
import SkLoader from '~/components/ui/SkLoader';

export default function FSAOnboardingModal(): JSXElement {
  const appData = useAppData();
  const intl = useIntl();
  const toaster = useToast();
  const fsaModalContext = useFSAModalContext();
  const [isLoading, setIsLoading] = createSignal<boolean>(false);
  const [isImageLoading, setIsImageLoading] = createSignal<boolean>(true);

  const activeSSOConnectionsQuery = createQuery(() => ({
    queryKey: getEnvActiveSsoConnectionListQueryKey(
      appData.selectedEnvironment?.id
    ),
    queryFn: async () => {
      return await searchConnections({
        enabled: true
      });
    },
    retry: retryAuthorizedRequests,
    enabled: false
  }));

  return (
    <Modal
      open={fsaModalContext.isFSAOnboardingModalOpen}
      size={ModalSizes.XXX_LARGE}
      type={ModalTypes.DIALOG}
    >
      {(closeModal) => (
        <>
          <div class={`flex gap-6`}>
            <div class={`min-h-[380px] min-w-[509px] relative`}>
              <Show when={isImageLoading()}>
                <SkLoader />
              </Show>
              <img
                src={FSAOnboardingLogo}
                alt={'Full-Stack Auth Spotlight Banner'}
                loading="eager"
                fetchpriority="high"
                onLoad={() => setIsImageLoading(false)}
              />
            </div>
            <div class={`flex flex-col justify-between grow`}>
              <div class={`flex flex-col gap-5`}>
                <h2 class={`text-md font-semibold`}>
                  <Localized translationKey={getTranslationKey(`header`)} />
                </h2>
                <p class={`text-sm`}>
                  <Localized
                    translationKey={getTranslationKey(
                      `fsa_features_description`
                    )}
                  />
                </p>
                <ul class={`flex flex-col gap-5`}>
                  <For each={FSA_FEATURES_TRANSLATION_KEYS}>
                    {(translationKey) => (
                      <li class={`pl-7 relative flex items-center`}>
                        <div class={`absolute left-0`}>
                          <CircleCheckIcon
                            width={24}
                            class={`fill-[#30A46C] text-bg-default`}
                          />
                        </div>
                        <Localized translationKey={translationKey} />
                      </li>
                    )}
                  </For>
                </ul>
              </div>
              <div class={`flex justify-between gap-4`}>
                <Link
                  href={externalLinks.kbArticles.fsaQuickstartGuide}
                  target={'_blank'}
                  class={`flex items-center text-sm text-link-primary hover:underline`}
                  data-testid={'fsa-banner-docs'}
                >
                  <Localized
                    translationKey={getTranslationKey(`fsa_docs_button_label`)}
                  />
                  <ArrowUpRightIcon
                    strokeWidth={1.5}
                    width={16}
                    class={`mt-[1px]`}
                  />
                </Link>
                <div class={`flex gap-4`}>
                  <Button
                    onClick={() => {
                      closeModal();
                      fsaModalContext.hideFSAOnboardingModal();
                    }}
                    variant={`outline`}
                    size={'sm'}
                    data-testid={'fsa-banner-cancel'}
                  >
                    <Localized translationKey={'cta_labels.cancel'} />
                  </Button>
                  <Button
                    variant={`solid`}
                    size={'sm'}
                    loading={isLoading()}
                    data-testid={'fsa-banner-activate'}
                    onClick={async () => {
                      try {
                        const isProdEnv =
                          appData.selectedEnvironment!.type ===
                          EnvironmentTypes.PRODUCTION;
                        setIsLoading(true);
                        await activeSSOConnectionsQuery.refetch();
                        const connections =
                          activeSSOConnectionsQuery.data as SearchConnectionsResponse;

                        // Removing Test Organization's Active SSO Connection from the count in Dev Env
                        const activeConnections = isProdEnv
                          ? connections.totalSize
                          : connections.totalSize - 1;

                        if (activeConnections > 0) {
                          fsaModalContext.openFSAOnboardingWarningModal();
                        } else {
                          await enableFSAWithSuccessToast({
                            envId: appData.selectedEnvironment!.id,
                            toaster,
                            intl
                          });
                        }
                      } catch (error) {
                        logAndToastFSAError({
                          error: error as Error,
                          toaster,
                          intl
                        });
                      } finally {
                        setIsLoading(false);
                        closeModal();
                        fsaModalContext.hideFSAOnboardingModal();
                      }
                    }}
                  >
                    <Localized
                      translationKey={'cta_labels.switch_to_full_stack_auth'}
                    />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
